import React, { useEffect, useState } from "react";
import { useNavigation, StackActions } from "@react-navigation/native";
import GameShowPreviewScreen from "./GameShowPreviewScreen";
import SignupWebScreen from "./SignupWebScreen";
import { AppScreens } from "../../Utility/AppScreens";
import { ROUTE_CATEGORIES } from "../../../utility/RouteProtectionConfig";

/**
 * Path patterns for different route categories
 * Maps URL patterns to the appropriate pre-auth screen to show
 */
const PATH_PATTERNS = {
  GAMESHOW: ["/events/game-show"],

  NOTIFICATIONS: ["/notifications", "/ticket-detail", "/ticket-history"],

  PROFILE: [
    "/profile",
    "/profile/change-avatar",
    "/profile/redeem",
    "/profile/transactions",
    "/profile/user",
    "/profile/purchase-tokens",
  ],

  EVENTS: [
    "/events",
    "/events/create/public",
    "/events/create/private",
    "/events/edit/public",
    "/events/edit/private",
    "/events/ongoing",
    "/events/past",
    "/events/stream-setup",
    "/events/ongoing-streaming-setup",
    "/events/go-live",
    "/events/live-stream",
    "/event-summary",
    "/private-event-summary",
  ],

  CHAT: ["/messages", "/messages/add-friends"],

  HELP: ["/help", "/help/disputes", "/help/contact-us", "/help/create-ticket"],

  DISPUTES: ["/disputes", "/disputes/detail", "/dispute-center"],

  SETTINGS: ["/settings"],

  SEARCH: ["/search", "/search-games", "/search-mobile"],

  DASHBOARD: ["/dashboard"],

  PURCHASE: ["/payment-success"],
};

const SetupScreen = ({ route }) => {
  // Get path from route params OR URL query string
  // React Navigation doesn't automatically parse query params like ?path=... into route.params
  // So we need to check both sources
  let pathFromParams = route.params?.path;

  console.log("🔍 SetupScreen: route.params:", route.params);
  console.log("🔍 SetupScreen: path from params:", pathFromParams);

  // If not in route.params, check URL query string (web only)
  if (!pathFromParams && typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    pathFromParams = urlParams.get("path");
    if (pathFromParams) {
      console.log(
        "🌐 SetupScreen: Got path from URL query string:",
        pathFromParams
      );
    }
  }

  // Decode if needed
  if (pathFromParams) {
    try {
      // Check if it's URL encoded
      if (pathFromParams.includes("%")) {
        pathFromParams = decodeURIComponent(pathFromParams);
        console.log("🔓 SetupScreen: Decoded path:", pathFromParams);
      }

      // Warn about corrupted URLs (but continue - we'll use fallback values)
      if (pathFromParams.includes("[…]") || pathFromParams.includes("[...]")) {
        console.error(
          "⚠️ SetupScreen: URL contains truncation markers! Data is incomplete."
        );
        console.error(
          '💡 Solution: Use the "Copy Link" button in your app for complete URLs!'
        );
      }
    } catch (e) {
      console.error("SetupScreen: Error decoding path:", e);
    }
  }

  const path = pathFromParams;
  const navigation = useNavigation();
  const [showComponent, setShowComponent] = useState("gameshow"); // Default to GameShow

  /**
   * 🔍 Check if path matches any patterns in the given array
   * @param {string} path - The path to check
   * @param {string[]} patterns - Array of patterns to match against
   * @returns {boolean} - True if path matches any pattern
   */
  const matchesPathPatterns = (path, patterns) => {
    if (!path) return false;
    return patterns.some((pattern) => path.includes(pattern));
  };

  /**
   * 🎯 Extract GameShow specific data from path
   * @param {string} path - The path to extract from
   * @returns {Object} - Extracted data with eventID and invite
   */
  const extractGameShowData = (path) => {
    const eventIDMatch = path.match(/eventID=([^&?]+)/);
    const inviteMatch = path.match(/invite=([^&?]+)/);

    return {
      eventID: eventIDMatch ? eventIDMatch[1] : null,
      invite: inviteMatch ? inviteMatch[1] : null,
    };
  };

  useEffect(() => {
    console.log("🔧 SetupScreen: Analyzing path parameter:", path);
    console.log("🔧 SetupScreen: Full route.params:", route.params);

    if (!path) {
      console.log("❌ No path parameter - defaulting to Signup");
      setShowComponent("signup");
      return;
    }

    // 🔍 Path Analysis - Check patterns in order of priority

    // ✅ PRIORITY 1: GameShow Paths (special handling for event sharing)
    if (matchesPathPatterns(path, PATH_PATTERNS.GAMESHOW)) {
      console.log("🎮 GameShow path detected:", path);

      // Extract GameShow specific data
      const gameShowData = extractGameShowData(path);
      console.log("🎯 Extracted GameShow data:", gameShowData);

      // Update route params if eventID found
      if (gameShowData.eventID) {
        route.params = {
          ...route.params,
          eventID: gameShowData.eventID,
          invite: gameShowData.invite,
        };
        console.log("✅ Updated route params for GameShow");
      }

      setShowComponent("gameshow");
      return;
    }

    // ✅ PRIORITY 2: All other protected routes - show signup
    const allProtectedPatterns = [
      ...PATH_PATTERNS.NOTIFICATIONS,
      ...PATH_PATTERNS.PROFILE,
      ...PATH_PATTERNS.EVENTS,
      ...PATH_PATTERNS.CHAT,
      ...PATH_PATTERNS.HELP,
      ...PATH_PATTERNS.DISPUTES,
      ...PATH_PATTERNS.SETTINGS,
      ...PATH_PATTERNS.SEARCH,
      ...PATH_PATTERNS.DASHBOARD,
      ...PATH_PATTERNS.PURCHASE,
    ];

    if (matchesPathPatterns(path, allProtectedPatterns)) {
      const category = Object.entries(PATH_PATTERNS).find(([key, patterns]) =>
        matchesPathPatterns(path, patterns)
      );
      console.log(
        `✅ Protected route detected - Category: ${category?.[0]}, Path: ${path}`
      );
      console.log(`📝 Showing signup screen with redirect path preserved`);
      setShowComponent("signup");
      return;
    }

    // ✅ DEFAULT FALLBACK: For unknown paths, show signup
    console.log("⚠️ Unknown path pattern - defaulting to signup for:", path);
    setShowComponent("signup");
  }, [path]);

  // 🎨 Component Rendering based on path analysis
  switch (showComponent) {
    case "signup":
      console.log("📧 Rendering SignupWebScreen for Notifications");

      // Extract session data from the notification URL path
      const sessionMatch = path?.match(/session=([^&?]+)/);
      const inviteMatch = path?.match(/invite=([^&?]+)/);

      // Decode eventId from session parameter
      let eventIdFromSession = null;

      if (sessionMatch) {
        try {
          const Base64 = require("js-base64").Base64;
          const decodedSession = Base64.decode(sessionMatch[1]);
          const sessionData = JSON.parse(decodedSession);
          eventIdFromSession = sessionData.eventId;
          console.log("📧 Decoded eventId from session:", eventIdFromSession);
        } catch (error) {
          console.error("❌ SetupScreen failed to decode session:", error);

          // Try robust extraction from corrupted/partial data
          try {
            console.log("🔧 SetupScreen: Attempting robust extraction...");
            const Base64 = require("js-base64").Base64;

            // Remove truncation markers
            let cleanSession = sessionMatch[1]
              .replace(/\[…\]/g, "")
              .replace(/\[\.\.\.\]/g, "");

            // Try to decode even if partial
            const partialDecoded = Base64.decode(cleanSession);
            console.log(
              "🔍 SetupScreen: Partial decoded:",
              partialDecoded.substring(0, 150)
            );

            // Extract eventId using regex
            const eventIdMatch = partialDecoded.match(
              /"eventId"\s*:\s*"([a-f0-9]+)"/i
            );
            if (eventIdMatch && eventIdMatch[1]) {
              eventIdFromSession = eventIdMatch[1];
              console.log(
                "✅ SetupScreen: Extracted eventId from corrupted data:",
                eventIdFromSession
              );
            }
          } catch (extractError) {
            console.error(
              "❌ SetupScreen: Robust extraction failed:",
              extractError
            );
          }
        }
      }

      // Build the route params to pass to SignupWebScreen
      const signupRouteParams = {
        ...route.params,
        isFromEventShare: true,
        eventShareURL: path, // Pass the full notification path
        eventId: eventIdFromSession, // Add decoded eventId
        session: sessionMatch ? sessionMatch[1] : null,
        invite: inviteMatch ? inviteMatch[1] : null,
      };

      console.log("📧 Passing to SignupWebScreen:", signupRouteParams);

      return (
        <SignupWebScreen route={{ ...route, params: signupRouteParams }} />
      );

    case "gameshow":
      console.log("🎮 Rendering GameShowPreviewScreen");

      // Extract eventID and invite from the game-show URL path
      const eventIDMatch = path?.match(/eventID=([^&?]+)/);
      const gameShowInviteMatch = path?.match(/invite=([^&?]+)/);

      const gameShowRouteParams = {
        ...route.params,
        eventID: eventIDMatch ? eventIDMatch[1] : null,
        invite: gameShowInviteMatch ? gameShowInviteMatch[1] : null,
      };

      console.log("🎮 Passing to GameShowPreviewScreen:", gameShowRouteParams);

      return (
        <GameShowPreviewScreen
          route={{ ...route, params: gameShowRouteParams }}
        />
      );

    default:
      console.log("📝 Rendering SignupWebScreen (default)");

      // For all other protected routes, show signup with path preserved
      const defaultRouteParams = {
        ...route.params,
        isFromProtectedRoute: true,
        redirectPath: path,
      };

      return (
        <SignupWebScreen route={{ ...route, params: defaultRouteParams }} />
      );
  }
};

export default SetupScreen;
